/**
 * useAgentChat Hook
 *
 * Manages SSE streaming communication with the equip-template agent.
 * Handles message sending, streaming responses, and session management.
 */

import { useState, useCallback, useRef } from 'react'
import type {
  AgentMessage,
  StreamEvent,
  ThinkingStage,
  SendMessageResponse,
} from '@/lib/agent-chat'

// Get backend API URL
const getBackendUrl = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  return ''
}

const AGENT_TYPE = 'equip-template'

interface UseAgentChatOptions {
  onHtmlUpdate?: (html: string) => void  // Callback when AI returns modified HTML
}

interface UseAgentChatResult {
  // State
  messages: AgentMessage[]
  isLoading: boolean
  isStreaming: boolean
  isReceivingContent: boolean  // True only when content chunks are arriving (for editor lock)
  thinkingStage: ThinkingStage | null
  thinkingMessage: string | null
  error: string | null
  sessionId: string | null

  // Actions
  sendMessage: (message: string, currentHtml: string, templateName?: string) => Promise<void>
  retryLastMessage: () => Promise<void>
  clearMessages: () => void
  clearError: () => void
}

export function useAgentChat(options: UseAgentChatOptions = {}): UseAgentChatResult {
  const { onHtmlUpdate } = options

  const [messages, setMessages] = useState<AgentMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isReceivingContent, setIsReceivingContent] = useState(false)
  const [thinkingStage, setThinkingStage] = useState<ThinkingStage | null>(null)
  const [thinkingMessage, setThinkingMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)

  // Track current streaming message content
  const streamingContentRef = useRef<string>('')
  const abortControllerRef = useRef<AbortController | null>(null)

  // Store last message params for retry
  const lastMessageParamsRef = useRef<{
    message: string
    currentHtml: string
    templateName?: string
  } | null>(null)

  const clearMessages = useCallback(() => {
    setMessages([])
    setSessionId(null)
    setError(null)
  }, [])

  const clearError = useCallback(() => setError(null), [])

  /**
   * Send a message to the agent and stream the response
   */
  const sendMessage = useCallback(async (
    message: string,
    currentHtml: string,
    templateName?: string
  ): Promise<void> => {
    // Store params for retry
    lastMessageParamsRef.current = { message, currentHtml, templateName }

    // Cancel any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    setIsLoading(true)
    setError(null)
    streamingContentRef.current = ''

    // Add user message
    const userMessage: AgentMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, userMessage])

    // Create placeholder for assistant message
    const assistantMessageId = `assistant-${Date.now()}`
    const assistantMessage: AgentMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    }
    setMessages(prev => [...prev, assistantMessage])

    try {
      const backendUrl = getBackendUrl()

      // Step 1: POST to create message and get stream URL
      const response = await fetch(`${backendUrl}/api/agent-chat/${AGENT_TYPE}/message`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          session_id: sessionId,
          context: {
            template_name: templateName || 'Email Template',
            current_html: currentHtml,
          },
        }),
      })

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`)
      }

      const { session_id, stream_url }: SendMessageResponse = await response.json()
      setSessionId(session_id)

      // Step 2: Connect to SSE stream
      setIsLoading(false)
      setIsStreaming(true)

      abortControllerRef.current = new AbortController()

      const streamResponse = await fetch(`${backendUrl}${stream_url}`, {
        credentials: 'include',
        signal: abortControllerRef.current.signal,
      })

      if (!streamResponse.ok) {
        throw new Error(`Stream connection failed: ${streamResponse.status}`)
      }

      const reader = streamResponse.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''

      // Process SSE stream
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Parse SSE events - handle both \n\n and \r\n\r\n line endings
        // (sse-starlette sends CRLF line endings)
        const events = buffer.split(/\r?\n\r?\n/)
        buffer = events.pop() || '' // Keep incomplete event in buffer

        for (const eventText of events) {
          if (!eventText.trim()) continue

          // SSE format: "data: {...}" - handle CRLF line endings
          const dataMatch = eventText.match(/^data: (.+)\r?$/m)
          if (!dataMatch) continue

          const eventData = dataMatch[1]

          try {
            const event: StreamEvent = JSON.parse(eventData)

            switch (event.event_type) {
              case 'content':
                // Mark that we're receiving content (for editor locking)
                if (!streamingContentRef.current) {
                  setIsReceivingContent(true)
                }
                streamingContentRef.current += event.content
                // Update the assistant message with accumulated content
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: streamingContentRef.current }
                    : msg
                ))
                break

              case 'thinking':
                setThinkingStage(event.stage)
                setThinkingMessage(event.message || null)
                break

              case 'error':
                setError(event.message)
                // Mark message as errored
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMessageId
                    ? { ...msg, isStreaming: false, error: event.message }
                    : msg
                ))
                break

              case 'done':
                // Finalize the message
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
                ))
                setIsReceivingContent(false)
                // Extract HTML from response and notify
                const finalContent = streamingContentRef.current
                if (onHtmlUpdate && finalContent.includes('<')) {
                  // The agent returns complete HTML
                  onHtmlUpdate(finalContent)
                }
                break
            }
          } catch (parseError) {
            console.error('Failed to parse SSE event:', parseError)
          }
        }
      }

    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // Request was cancelled, not an error
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
      setError(errorMessage)

      // Mark message as errored
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? { ...msg, isStreaming: false, error: errorMessage }
          : msg
      ))
    } finally {
      setIsLoading(false)
      setIsStreaming(false)
      setIsReceivingContent(false)
      setThinkingStage(null)
      setThinkingMessage(null)
      abortControllerRef.current = null
    }
  }, [sessionId, onHtmlUpdate])

  /**
   * Retry the last message sent
   */
  const retryLastMessage = useCallback(async (): Promise<void> => {
    if (!lastMessageParamsRef.current) {
      console.warn('No message to retry')
      return
    }

    const { message, currentHtml, templateName } = lastMessageParamsRef.current
    await sendMessage(message, currentHtml, templateName)
  }, [sendMessage])

  return {
    messages,
    isLoading,
    isStreaming,
    isReceivingContent,
    thinkingStage,
    thinkingMessage,
    error,
    sessionId,
    sendMessage,
    retryLastMessage,
    clearMessages,
    clearError,
  }
}
